import React, { useReducer, useCallback } from "react";
import SectionHeader from "../../../../components/SectionHeader/SectionHeader";
import LoadingOverlay from "react-loading-overlay";
import AddOrganisationForm from "./AddOrganisationForm";
import ErrorFooter from "../../../../components/ErrorFooter/ErrorFooter";
import "../../../../components/ErrorFooter/ErrorFooter.css";

import { useHistory } from "react-router-dom";
import { request, handleErrors } from "../../../../services/helpers";
import debounce from "lodash.debounce";
import { useForm } from "react-hook-form";
import { object, number, string } from "yup";
import { yupResolver } from "@hookform/resolvers";

const SET_FORM_LOADING = "SET_FORM_LOADING";
const GET_ORGANISATION_SUCCESS = "GET_ORGANISATION_SUCCESS";
const GET_ORGANISATION_FAILURE = "GET_ORGANISATION_FAILURE";
const FORM_SUBMIT_SUCCESS = "FORM_SUBMIT_SUCCESS";
const FORM_SUBMIT_FAILURE = "FORM_SUBMIT_FAILURE";
const CLEAR_ERRORS = "CLEAR_ERRORS";

const initialState = {
  organisation: null,
  gstError: null,
  panError: null,
  loading: false,
  error: "",
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_FORM_LOADING:
      return {
        ...state,
        loading: action.payload === false ? action.payload : true,
        error: "",
      };
    case GET_ORGANISATION_SUCCESS: {
      return {
        ...state,
        organisation: action.payload.organisation,
        [action.payload.clearErrorKey]: null,
        loading: false,
      };
    }
    case GET_ORGANISATION_FAILURE: {
      return {
        ...state,
        [action.payload.type]: action.payload.error,
        loading: false,
        organisation: null,
      };
    }

    case FORM_SUBMIT_SUCCESS: {
      return {
        ...state,
        loading: false,
      };
    }

    case FORM_SUBMIT_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    }

    case CLEAR_ERRORS: {
      return {
        ...state,
        [action.payload]: null,
      };
    }

    default:
      return state;
  }
};

const validationSchema = object().shape({
  registeredCompanyName: string().required(
    "Registered Organisation Name is required"
  ),
  registeredAddress: string().required(
    "Registered Organisation Address is required"
  ),
  phoneNumber: number()
    .typeError("Phone number is a required field")
    .required("Organisation's Phone number is required."),
  registeredEmail: string()
    .email()
    .required("Organisation's email ID is required."),
});

const AddOrganisation = () => {
  const [formState, dispatch] = useReducer(reducer, initialState);

  const history = useHistory();
  const { error, loading, organisation, gstError, panError } = formState;
  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const getOrganisation = useCallback(
    async (type, key) => {
      let max = type === "gst" ? 15 : 10;
      if (key === "") {
        if (type === "gst") {
          methods.reset({});
        }
        dispatch({ type: CLEAR_ERRORS, payload: `${type}Error` });
      }
      if (key.length < max) return;
      dispatch({ type: SET_FORM_LOADING });
      dispatch({ type: CLEAR_ERRORS, payload: `${type}Error` });
      try {
        const res = await request.get(
          `/organizations/searchOrganization/?type=${type}Number&text=${key}`
        );
        // console.log("response", res);
        const values = res.data[0];
        if (Object.keys(values).length > 0) {
          methods.reset(values);
          dispatch({ type: CLEAR_ERRORS, payload: `${"pan"}Error` });
        }
        dispatch({
          type: SET_FORM_LOADING,
          payload: false,
        });
      } catch (e) {
        // console.log("error", e);
        dispatch({
          type: GET_ORGANISATION_FAILURE,
          payload: {
            type: `${type}Error`,
            error: handleErrors(e),
          },
        });
      }
    },
    [dispatch]
  );
  const debouncedGetOrganisation = useCallback(debounce(getOrganisation, 500), [
    dispatch,
  ]);

  const handleSubmitForm = async (data) => {
    try {
      dispatch({ type: SET_FORM_LOADING });
      const res = await request.post(`/organizations`, {
        ...data,
        name: data.registeredCompanyName,
      });

      methods.reset();
      history.push({
        pathname: "/finish",
        state: {
          data: {
            title: "Organisation Created!",
            redirectTo: "/add-membership",
            completed: true,
            subTitle: "Organisation Name",
            goTo: "Add Membership",
            name: res.data.registeredCompanyName,
          },
        },
      });
      dispatch({ type: SET_FORM_LOADING, payload: false });
    } catch (e) {
      dispatch({
        type: FORM_SUBMIT_FAILURE,
        payload: handleErrors(e),
      });
    }
  };

  return (
    <div className="bg-formBackground">
      <LoadingOverlay
        className=""
        active={loading}
        spinner={loading}
        text="Loading please wait"
        // className="flex flex-col flex-1"
      >
        <div className="bg-formBackground">
          <SectionHeader title={"Add a New Organisation"} />
          {/* Organisation Form */}

          <form
            onSubmit={methods.handleSubmit(handleSubmitForm)}
            className="pb-24 flex flex-col flex-1 h-screen justify-between"
          >
            <div className="lg:px-32 px-8 mt-16">
              <div className="lg:flex">
                <div className="lg:mr-6 max-w-xs">
                  <h4 className="font-medium text-2xl leading-7">
                    Organisation Details
                  </h4>
                </div>
                <div className="lg:mt-0 my-8 w-full">
                  <div>
                    <AddOrganisationForm
                      debouncedGetOrganisation={debouncedGetOrganisation}
                      formState={formState}
                      methods={methods}
                    />
                  </div>
                </div>
              </div>
              <hr className="border border-b-0 border-black mx-24 border-opacity-25" />
              {/* Membership Details Form */}
            </div>
            {error && (
              <div className="error-footer">
                <ErrorFooter message={error} />
              </div>
            )}

            <div className="shadow-lg w-full flex md:justify-end justify-center py-4 px-8 border border-b-2 items-center">
              <button
                disabled={loading || gstError || panError}
                type="submit"
                className="bg-yellow-500 py-4 px-16 rounded font-bold text-sm tracking-widest leading-4 uppercase outline-none"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default AddOrganisation;
